// src/components/TaskPage/Modals/RequestTaskClosureModal.tsx

import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import { TransactionReasonResponseDto } from "../../../../interfaces/adminRequests/ReasonTypes";
import { getReasonsByType } from "../../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import { CreateAdministrativeTransactionRequest } from "../../../../interfaces/adminRequests/CreateAdministrativeTransactionRequest";
import { RequestTaskClosureAsync } from "../../../../services/taskServices/TaskService";
import { AxiosError } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

interface RequestTaskClosureModalProps {
  show: boolean;
  onHide: () => void;
  taskId: number; // The numerical task ID
  onSuccess: () => void; // Callback to refresh parent component (TaskPage)
}

const RequestTaskClosureModal: React.FC<RequestTaskClosureModalProps> = ({
  show,
  onHide,
  taskId,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [reasons, setReasons] = useState<TransactionReasonResponseDto[]>([]);
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [comment, setComment] = useState<string>(""); // For the textarea
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Fetch reasons when the modal is shown and currentUser is available
  useEffect(() => {
    const fetchReasons = async () => {
      if (!currentUser || !show) return;

      try {
        const token = await currentUser.getIdToken();
        const result = await getReasonsByType(token, "CLOSURE"); // Fetch reasons of type 'CLOSURE'

        if ("error" in result) {
          setLocalError(result.error);
          setReasons([]);
          toast.error(`Error loading reasons: ${result.error}`);
        } else {
          setReasons(result);
          setLocalError(null);
        }
      } catch (err: any) {
        setLocalError("Failed to load closure reasons.");
        toast.error("Failed to load closure reasons.");
        console.error("Error fetching closure reasons:", err);
      }
    };

    fetchReasons();
  }, [show, currentUser]); // Re-run when modal shown or user changes

  const handleSubmit = async () => {
    if (!currentUser) {
      setLocalError("User not authenticated.");
      return;
    }
    if (!selectedReasonId) {
      setLocalError("Please select a reason for closure.");
      return;
    }

    setIsSubmitting(true);
    setLocalError(null);

    try {
      const authToken = await currentUser.getIdToken();

      const newRequestPayload: CreateAdministrativeTransactionRequest = {
        requesterId: currentUser.uid,
        transactionTypeId: "Task Deletion Request", // Backend's expected type name
        statusId: "PENDING", // Backend's expected status name
        taskId: taskId,
        transactionReasonId: selectedReasonId, // Send the selected reason ID
        transactionReasonText: comment, // Send the comment text
      };

      // Call the create service
      // Note: your backend's /api/tasks/{taskId}/request-close endpoint is used for this.
      // It handles either creating an admin request OR directly closing.
      // This front-end code will directly call the tasks endpoint.

      const res = await RequestTaskClosureAsync(
        authToken,
        taskId,
        newRequestPayload
      );
      if (res.success) {
        toast.success(res.message);
        onSuccess(); // Call callback to refresh parent component data
        onHide(); // Close modal
      } else {
        toast.error(res.message);
        setLocalError(res.message);
      }
    } catch (error: any) {
      console.error("Error submitting task deletion request:", error);
      const errorMsg =
        error instanceof AxiosError
          ? error.response?.data.message
          : error.message || "An unexpected error occurred. Please try again.";
      toast.error(errorMsg);
      setLocalError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <div className="modal-activity-reject-header">
        <h2 className="modal-activity-reject-title">Request Task Deletion</h2>
        <button
          className="modal-activity-reject-close"
          onClick={onHide}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <Modal.Body>
        <Form>
          {localError && (
            <Alert
              variant="danger"
              onClose={() => setLocalError(null)}
              dismissible
            >
              {localError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              Reason for Deletion <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={selectedReasonId || ""}
              onChange={(e) =>
                setSelectedReasonId(parseInt(e.target.value, 10) || null)
              }
              disabled={isSubmitting}
            >
              <option value="">Select a reason</option>
              {reasons.map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.reasonText}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Additional Comments (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Provide any additional details..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="btn btn-secondary cancel-btn"
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          className="btn btn-primary submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Request"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RequestTaskClosureModal;
